import { formatPreparos, qtdComNome, type PrepCount } from "@workspace/pricing";
import { formatCurrency } from "@/lib/utils";

// Contador do pacote de escolhas (proposta aprovada pelo Pedro, 15/10/2026; nasceu «Contar preparos»): fixo no topo da
// lista de opções, na vitrine e no PDV. Abaixo dos inclusos mostra «X de N {itens}» e o que falta; acima, os extras e
// o valor; a barra separa inclusos (verde) de extras. Os textos usam o nome que a loja dá aos itens e não têm gênero
// nem singular (a loja só informa o plural). No PDV (`pdv`), fora de N..M ele só avisa — a loja fecha mesmo assim
// (Pedro: «só avisar»). No «até N no preço» (`required` false) não falta nada abaixo de N: a etiqueta diz «no preço».
// Sem máximo (`max` null), nunca passa e a barra se mede pelos inclusos.
export function ContadorDePreparos({ c, pdv = false }: { c: PrepCount; pdv?: boolean }) {
  const n = formatPreparos;
  const acima = c.total > c.included + 1e-9;
  const aMais = c.total - c.included;
  const tag = c.overMax
    ? { cls: "bg-destructive/10 text-destructive", text: `passou de ${n(c.max ?? 0)}` }
    : acima
      ? { cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200", text: `${c.extras} ${c.extras === 1 ? "extra" : "extras"} +${formatCurrency(c.extrasValue)}` }
      : c.missing > 0
        ? { cls: "bg-muted text-muted-foreground", text: `${c.missing <= 1 ? "falta" : "faltam"} ${n(c.missing)}` }
        : { cls: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200", text: c.required ? "completo" : "no preço" };
  const base = Math.max(c.max ?? c.included, c.total, 1);
  const inc = (Math.min(c.total, c.included) / base) * 100;
  const ext = (Math.max(0, aMais) / base) * 100;
  const foraDoPacote = c.missing > 0 || c.overMax;
  const cabeMais = c.max == null || c.max > c.included;
  const ate = c.max != null ? ` (até ${n(c.max)})` : "";
  return (
    <div className="rounded-2xl border border-border bg-card p-3 shadow-sm space-y-2" data-testid="prep-counter-card">
      <div className="flex items-baseline justify-between gap-2">
        <span className="font-display text-xl font-bold tabular-nums" data-testid="prep-counter-total">
          {acima ? `${n(c.total)} ${c.unitName}` : `${n(c.total)} de ${qtdComNome(c.included, c.unitName)}`}
        </span>
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap ${tag.cls}`} data-testid="prep-counter-tag">{tag.text}</span>
      </div>
      <div className="flex h-2 overflow-hidden rounded-full bg-muted">
        <i className="block h-full bg-emerald-600" style={{ width: `${inc}%` }} />
        <i className="block h-full bg-amber-500" style={{ width: `${ext}%` }} />
      </div>
      <p className="text-xs text-muted-foreground" data-testid="prep-counter-hint">
        {acima ? (
          <>
            {n(c.included)} {c.required ? "do pacote" : "no preço"} + {n(aMais)} a mais.
            {aMais % 1 !== 0 ? <b className="text-foreground"> Meio já conta como 1 extra.</b> : null}
            {c.max != null ? ` Cabe até ${n(c.max)}.` : null}
          </>
        ) : c.required && cabeMais ? (
          <>Depois de {n(c.included)}, cada extra custa <b className="text-foreground">{formatCurrency(c.extraPrice)}</b>{ate}.</>
        ) : !c.required && cabeMais && c.extraPrice > 0 ? (
          <>Até {n(c.included)} no preço; depois, cada extra custa <b className="text-foreground">{formatCurrency(c.extraPrice)}</b>{ate}.</>
        ) : (
          <>{c.required ? `O pacote tem ${qtdComNome(c.included, c.unitName)}.` : `Até ${qtdComNome(c.included, c.unitName)} no preço.`}</>
        )}
      </p>
      {pdv && foraDoPacote && (
        <p className="text-xs font-semibold text-amber-700 dark:text-amber-300" data-testid="prep-counter-pdv">
          {c.overMax ? `Passou de ${n(c.max ?? 0)}.` : `${c.missing <= 1 ? "Falta" : "Faltam"} ${n(c.missing)} para completar o pacote.`} No PDV dá para fechar mesmo assim.
        </p>
      )}
    </div>
  );
}
